import type { ComponentType } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDownIcon, Loader2 } from 'lucide-react'

import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox'
import { PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

/** Stands in for "no selection" — cmdk items need a non-empty value. */
const NONE = '__none__'

export interface SearchSelectOption {
  value: string
  label: string
}

export interface SearchSelectProps {
  /** Selected option value, or '' for no selection. */
  value: string
  onChange: (value: string) => void
  options: SearchSelectOption[]
  /** Shown in the trigger when nothing is selected. */
  placeholder: string
  /** Label for the selected value when it isn't among `options` (yet). */
  fallbackLabel?: string
  /** Offers the placeholder as a first option that clears the selection. */
  clearable?: boolean
  /** Icon shown in front of every option. */
  icon?: ComponentType<{ className?: string }>
  searchPlaceholder?: string
  emptyText?: string
  /** Shows a spinner in the trigger and the list while options load. */
  loading?: boolean
  /** Fires when the trigger is pressed — lets the caller fetch options lazily. */
  onOpen?: () => void
  /**
   * Fires as the search text changes, for options too many to load at once —
   * the caller fetches the matches and passes them in as `options`.
   */
  onSearchChange?: (term: string) => void
  /** Shows a spinner in the list only, while the matches for a search load. */
  searching?: boolean
  disabled?: boolean
  className?: string
}

/** Single-value dropdown with a search field, styled like the select triggers. */
export function SearchSelect({
  value,
  onChange,
  options,
  placeholder,
  fallbackLabel,
  clearable,
  icon: Icon,
  searchPlaceholder,
  emptyText,
  loading,
  onOpen,
  onSearchChange,
  searching,
  disabled,
  className,
}: SearchSelectProps) {
  const { t } = useTranslation()
  const label = options.find((o) => o.value === value)?.label ?? (value ? (fallbackLabel ?? value) : placeholder)

  return (
    <Combobox
      value={value}
      onValueChange={(v) => {
        onChange(v === NONE ? '' : v)
        // Picking clears the search text, so the caller's search starts over too.
        onSearchChange?.('')
      }}
      disabled={disabled}
    >
      <PopoverTrigger asChild>
        <Button
          variant="field"
          disabled={disabled}
          onPointerDown={onOpen}
          className={cn('w-full min-w-0 justify-between px-3', className)}
        >
          {loading ? (
            <span className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="size-3.5 animate-spin" />
              {t('common.loading')}
            </span>
          ) : (
            <span className="truncate">{label}</span>
          )}
          <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <ComboboxContent>
        <ComboboxInput
          placeholder={searchPlaceholder ?? t('common.search')}
          onValueChange={onSearchChange}
        />
        <ComboboxList>
          {loading || searching ? (
            <div className="flex items-center gap-2 py-1.5 pr-8 pl-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 shrink-0 animate-spin" />
              {t('common.loading')}
            </div>
          ) : (
            <>
              <ComboboxEmpty>{emptyText ?? t('common.noResults')}</ComboboxEmpty>
              {clearable && (
                <ComboboxItem value={NONE} keywords={[placeholder]}>
                  <span>{placeholder}</span>
                </ComboboxItem>
              )}
              {options.map((option) => (
                <ComboboxItem key={option.value} value={option.value} keywords={[option.label]}>
                  {Icon && <Icon className="size-4 shrink-0 text-muted-foreground" />}
                  <span className="truncate">{option.label}</span>
                </ComboboxItem>
              ))}
            </>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )
}
